import { useEffect, useRef, useState } from "react";
import {
  clearSetupDraft,
  readSetupDraft,
  sessionStorageOrUndefined,
  writeSetupDraft,
} from "@/lib/setup-draft-storage";

export function useSetupDraft<T extends object>(
  key: string,
  fallback: T,
): [T, (next: T | ((prev: T) => T)) => void, () => void] {
  const [value, setValue] = useState<T>(() => (readSetupDraft<T & Record<string, unknown>>(sessionStorageOrUndefined(), key) as T | null) ?? fallback);
  const activeKey = useRef(key);
  const fallbackRef = useRef(fallback);

  fallbackRef.current = fallback;

  useEffect(() => {
    if (activeKey.current !== key) return;
    writeSetupDraft(sessionStorageOrUndefined(), key, value as Record<string, unknown>);
  }, [key, value]);

  useEffect(() => {
    activeKey.current = key;
    const stored = readSetupDraft<T & Record<string, unknown>>(sessionStorageOrUndefined(), key);
    setValue((stored as T | null) ?? fallbackRef.current);
  }, [key]);

  const clear = () => {
    clearSetupDraft(sessionStorageOrUndefined(), key);
    setValue(fallbackRef.current);
  };

  return [value, setValue, clear];
}
